import { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import chatSupportCloseButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/chat_support_close_button_click';
import { ChatikProvider, ChatikProviderProps } from '@hh.ru/chatik-integration';
import { CounterUpdater, OnUpdate } from '@hh.ru/chatik-integration/lib/CounterUpdater';
import { websocket } from '@hh.ru/chatik-integration/lib/Websocket';

import useBreakpoint, { Breakpoint } from 'bloko/common/hooks/useBreakpoint';
import modalHelper from 'bloko/common/modalHelper';

import useInitParams from 'utils/chatik/ChatikIntegration/hooks/useInitParams';

import { setChatikUnreadSupportCount } from 'store/autoReducers';
import { useSelector } from 'store/utils';

type OnOpenCallback = Required<ChatikProviderProps>['onOpen'];
type OnCloseCallback = Required<ChatikProviderProps>['onClose'];

const ChatikIntegration: FC<PropsWithChildren> = ({ children }) => {
    const domain = window.location.hostname.replace('clickme.', '');
    const userId = useSelector((state) => state.user.user?.id) ?? '';

    const chatikOrigin = `https://chatik.${domain}`;
    const websocketOrigin = `https://websocket.${domain}`;

    const isMobile = useBreakpoint() === Breakpoint.XS;
    const dispatch = useDispatch();

    const initParams = useInitParams(chatikOrigin);

    useEffect(() => {
        if (websocketOrigin) {
            websocket.connect({ origin: websocketOrigin });
        }
    }, [websocketOrigin]);

    const handleOpen = useCallback<OnOpenCallback>(() => {
        if (isMobile) {
            modalHelper.disableScroll();
        }
    }, [isMobile]);

    const handleClose = useCallback<OnCloseCallback>(() => {
        if (isMobile) {
            modalHelper.enableScroll();
        }
        chatSupportCloseButtonClick();
    }, [isMobile]);

    const handleCounterUpdate: OnUpdate = useCallback(
        ({ unreadSupportCount }) => {
            dispatch(setChatikUnreadSupportCount(typeof unreadSupportCount === 'number' ? unreadSupportCount : 0));
        },
        [dispatch]
    );

    return (
        <ChatikProvider
            initParams={initParams}
            onOpen={handleOpen}
            onClose={handleClose}
            offsetElementSelector=".header"
        >
            {children}
            <CounterUpdater
                counterId={String(userId)}
                onUpdate={handleCounterUpdate}
                websocketOrigin={websocketOrigin}
                chatikOrigin={chatikOrigin}
            />
        </ChatikProvider>
    );
};

export default ChatikIntegration;
